/** SHERRYS cinematic hero — standalone Shopify section / accessible carousel. */
(() => {
  if (customElements.get('snh-slider')) return;

  class SherrysNishatHero extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.slides = [...this.querySelectorAll('[data-snh-slide]')];
      this.pages = [...this.querySelectorAll('[data-snh-page]')];
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.duration = Math.max(3000, Number(this.dataset.autoplaySpeed) || 5000);
      this.remaining = this.duration;
      this.loop = this.dataset.loop !== 'false';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pauses = new Set();
      this.timer = null;
      this.timerEnd = 0;
      this._transitionCleanup = null;

      this.querySelectorAll('[data-snh-next]').forEach(button => button.addEventListener('click', () => this.next(true), { signal }));
      this.querySelectorAll('[data-snh-prev]').forEach(button => button.addEventListener('click', () => this.previous(true), { signal }));
      this.pages.forEach((page, i) => page.addEventListener('click', () => this.goTo(i, true), { signal }));

      const viewport = this.querySelector('[data-snh-viewport]');
      if (viewport) {
        let down = null;
        viewport.addEventListener('pointerdown', event => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          down = { x: event.clientX, y: event.clientY, target: event.target, time: performance.now() };
        }, { signal, passive: true });
        viewport.addEventListener('pointerup', event => {
          if (!down) return;
          const dx = event.clientX - down.x;
          const dy = event.clientY - down.y;
          if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4 && performance.now() - down.time < 1100) {
            // Prevent the accidental banner click that sometimes follows a swipe.
            this._suppressClickUntil = performance.now() + 450;
            if (dx < 0) this.next(true); else this.previous(true);
          }
          down = null;
        }, { signal, passive: true });
        viewport.addEventListener('pointercancel', () => { down = null; }, { signal });
        viewport.addEventListener('click', event => {
          if (this._suppressClickUntil && performance.now() < this._suppressClickUntil && event.target.closest('a')) {
            event.preventDefault();
            event.stopPropagation();
          }
        }, { signal, capture: true });
      }

      if (this.dataset.pauseHover === 'true') {
        this.addEventListener('pointerenter', () => this.pause('hover'), { signal });
        this.addEventListener('pointerleave', () => this.resume('hover'), { signal });
        this.addEventListener('focusin', () => this.pause('focus'), { signal });
        this.addEventListener('focusout', event => {
          if (!this.contains(event.relatedTarget)) this.resume('focus');
        }, { signal });
      }

      document.addEventListener('visibilitychange', () => document.hidden ? this.pause('document') : this.resume('document'), { signal });
      this.reducedMotion.addEventListener?.('change', () => this.syncMotion(), { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const visible = entries[0]?.isIntersecting;
          if (visible) this.resume('viewport'); else this.pause('viewport');
        }, { threshold: 0.15 });
        this.observer.observe(this);
      }
      this.querySelector('[data-snh-scroll]')?.addEventListener('click', event => {
        const next = this.closest('.shopify-section')?.nextElementSibling;
        if (next) {
          event.preventDefault();
          next.scrollIntoView({ behavior: this.reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
        }
      }, { signal });

      // Shopify Theme Editor: reveal a selected block and stop autoplay during editing.
      this.addEventListener('shopify:block:select', event => {
        const block = event.target.closest('[data-block-id]');
        if (!block) return;
        this.pause('editor');
        this.goTo(this.slides.indexOf(block), true);
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => this.resume('editor'), { signal });
      if (window.Shopify?.designMode) this.pause('editor');
      if (document.hidden) this.pause('document');
      this.syncMotion();
    }

    disconnectedCallback() {
      this._initialized = false;
      this.controller?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this._transitionCleanup);
    }

    syncMotion() {
      if (this.reducedMotion.matches) this.pause('motion'); else this.resume('motion');
      this.autoplay ? this.classList.add('is-playing') : this.classList.remove('is-playing');
      this.resetProgress();
      this.restartCycle();
    }

    pause(reason) {
      this.pauses.add(reason);
      if (this.timer) {
        this.remaining = Math.max(0, this.timerEnd - performance.now());
        clearTimeout(this.timer);
        this.timer = null;
      }
      this.classList.add('is-paused');
    }

    resume(reason) {
      this.pauses.delete(reason);
      if (this.pauses.size === 0) {
        this.classList.remove('is-paused');
        this.restartCycle(false);
      }
    }

    restartCycle(reset = true) {
      clearTimeout(this.timer);
      this.timer = null;
      if (reset) this.remaining = this.duration;
      if (!this.autoplay || this.pauses.size || (this.index === this.slides.length - 1 && !this.loop)) return;
      this.timerEnd = performance.now() + this.remaining;
      this.timer = setTimeout(() => this.next(false), this.remaining);
    }

    resetProgress() {
      const currentFill = this.pages[this.index]?.querySelector('.snh__page-fill');
      if (!currentFill || this.dataset.navStyle !== 'progress') return;
      currentFill.style.animation = 'none';
      // Force a new CSS keyframe cycle for this slide after manual navigation.
      void currentFill.offsetWidth;
      currentFill.style.removeProperty('animation');
    }

    next(manual) { this.goTo(this.index + 1, manual); }
    previous(manual) { this.goTo(this.index - 1, manual); }

    goTo(to, manual = false) {
      if (this.slides.length < 2) return;
      if (!this.loop && (to < 0 || to >= this.slides.length)) {
        this.restartCycle();
        return;
      }
      const nextIndex = (to + this.slides.length) % this.slides.length;
      if (nextIndex === this.index) return;
      const oldIndex = this.index;
      const old = this.slides[oldIndex];
      const incoming = this.slides[nextIndex];
      this.dataset.direction = to < oldIndex ? 'prev' : 'next';
      clearTimeout(this._transitionCleanup);
      for (const slide of this.slides) {
        if (slide !== old && slide !== incoming) slide.classList.remove('is-exiting', 'is-active');
      }
      old.classList.remove('is-active');
      old.classList.add('is-exiting');
      old.setAttribute('aria-hidden', 'true');
      old.inert = true;
      incoming.classList.remove('is-exiting');
      incoming.classList.add('is-active');
      incoming.setAttribute('aria-hidden', 'false');
      incoming.inert = false;
      this.index = nextIndex;
      this.pages.forEach((page, i) => {
        const current = i === nextIndex;
        page.classList.toggle('is-current', current);
        if (current) page.setAttribute('aria-current', 'true'); else page.removeAttribute('aria-current');
      });
      const count = this.querySelector('[data-snh-count]');
      if (count) count.textContent = String(nextIndex + 1).padStart(2, '0');
      this.resetProgress();
      this.restartCycle();
      const transition = this.reducedMotion.matches ? 0 : Math.min(2000, parseInt(getComputedStyle(this).getPropertyValue('--snh-duration')) || 650);
      this._transitionCleanup = setTimeout(() => old.classList.remove('is-exiting'), transition + 80);
      this.dispatchEvent(new CustomEvent('snh:change', { detail: { index: nextIndex, manual } }));
    }
  }
  customElements.define('snh-slider', SherrysNishatHero);
})();
